import {
  AnalyticsActionCategories,
  AnalyticsActionTypes,
} from '@clarity-website/common/Analytics';
import { useAnalytics } from '@clarity-website/common/useAnalytics';
import {
  TransientIdMapping,
  TransientIdsBySheetId,
  TransientOverrideBySheetId,
  TransientStorageBase,
} from '@clarity-website/reports/transient/transient-storage-base';
import { useCallback } from 'react';

/**
 * Interface for the sheet structure in the GQL format
 */
export interface TransientMappingSheet {
  id: string;
  reportTemplates: {
    id: string;
    transientIds: string[];
  }[];
}

/**
 * Interface for the GQL format of transient ID mapping
 */
export interface TransientMappingGQL {
  sheets: TransientMappingSheet[];
}

/**
 * Cell drill-down analytics constants and utilities
 */
const CELL_DRILL_DOWN_PREFIX = 'cell drill-down';

const cellDrillDownSuffixes = {
  CELL_CLICKED: 'clicked',
  REVIEW_ROW_LEVEL: 'review row-level table',
  EASY_MODE: 'easy-mode dropdown',
  ADVANCED_EDITING: 'advanced editing',
  DELETE_TABLE: 'delete table',
  NO_ACCESS: 'no access',
  SUBMIT_SIM: 'submit sim for access',
} as const;

export const CellDrillDownEventCategories = Object.freeze(
  Object.fromEntries(
    Object.entries(cellDrillDownSuffixes).map(([key, suffix]) => [
      key,
      `${CELL_DRILL_DOWN_PREFIX} ${suffix}`,
    ]),
  ),
) as Record<keyof typeof cellDrillDownSuffixes, string>;

/**
 * Custom hook for recording cell drill-down analytics with consistent base attributes
 * @returns A function that accepts custom attributes and records analytics with required base attributes
 */
export function useCellDrillDownAnalytics() {
  const { record } = useAnalytics();

  return useCallback(
    (customAttributes: Record<string, any>) => {
      record({
        name: 'cell-drill-down',
        attributes: {
          type: AnalyticsActionTypes.CLICK,
          actionCategory: AnalyticsActionCategories.CELL_DRILL_DOWN,
          ...customAttributes,
        },
      });
    },
    [record],
  );
}

/**
 * Ensures nested object path exists, initializing missing levels
 * @param obj The root object to ensure path in
 * @param reportGroupId The report group ID
 * @param sheetId The sheet ID
 * @param reportTemplateId Optional report template ID
 * @param defaultValue Default value for the final level (if reportTemplateId provided)
 */
export function ensureNestedPath<T>(
  obj: any,
  reportGroupId: string,
  sheetId: string,
  reportTemplateId?: string,
  defaultValue?: T,
): void {
  // Initialize nested objects if they don't exist
  if (!obj[reportGroupId]) {
    obj[reportGroupId] = {};
  }

  if (!obj[reportGroupId][sheetId]) {
    obj[reportGroupId][sheetId] = {};
  }

  if (reportTemplateId && !obj[reportGroupId][sheetId][reportTemplateId]) {
    obj[reportGroupId][sheetId][reportTemplateId] = defaultValue;
  }
}

/**
 * Cleans up empty nested objects after removal operations
 * @param mapping The mapping object to clean up
 * @param reportGroupId The report group ID
 * @param sheetId The sheet ID
 * @param reportTemplateId Optional report template ID
 */
export function cleanupEmptyObjects(
  mapping: any,
  reportGroupId: string,
  sheetId: string,
  reportTemplateId?: string,
): void {
  // Clean up empty objects from most nested to least nested
  if (reportTemplateId && mapping[reportGroupId]?.[sheetId]) {
    const reportTemplateValue =
      mapping[reportGroupId][sheetId][reportTemplateId];
    if (
      (Array.isArray(reportTemplateValue) &&
        reportTemplateValue.length === 0) ||
      reportTemplateValue === undefined ||
      reportTemplateValue === null
    ) {
      delete mapping[reportGroupId][sheetId][reportTemplateId];
    }
  }

  // Clean up sheet level if empty
  if (
    mapping[reportGroupId]?.[sheetId] &&
    Object.keys(mapping[reportGroupId][sheetId]).length === 0
  ) {
    delete mapping[reportGroupId][sheetId];
  }

  // Clean up report group level if empty
  if (
    mapping[reportGroupId] &&
    Object.keys(mapping[reportGroupId]).length === 0
  ) {
    delete mapping[reportGroupId];
  }
}

/**
 * Finds the source report template ID for a given transient ID
 * @param storage The storage instance to use for operations
 * @param reportGroupId The ID of the report group
 * @param sheetId The ID of the sheet
 * @param transientId The transient ID to find the source for
 * @returns The source report template ID or undefined if not found
 */
export function findSourceReportTemplateId(
  storage: TransientStorageBase<TransientIdMapping>,
  reportGroupId: string,
  sheetId: string,
  transientId: string,
): string | undefined {
  const mapping = storage.getMapping();

  if (!mapping[reportGroupId]?.[sheetId]) {
    return undefined;
  }

  const reportTemplates = mapping[reportGroupId][sheetId];
  const entry = Object.entries(reportTemplates).find(([_, transientIds]) =>
    transientIds.includes(transientId),
  );

  return entry ? entry[0] : undefined;
}

/**
 * Finds the source report template ID for a given override transient ID
 * @param storage The storage instance to use for operations
 * @param reportGroupId The ID of the report group
 * @param sheetId The ID of the sheet
 * @param transientId The transient ID to find the source for
 * @returns The source report template ID or undefined if not found
 */
export function findSourceReportTemplateIdForOverride(
  storage: TransientStorageBase<TransientIdMapping>,
  reportGroupId: string,
  sheetId: string,
  transientId: string,
): string | undefined {
  const mapping = storage.getOverrideMapping();

  if (!mapping[reportGroupId]?.[sheetId]) {
    return undefined;
  }

  const reportTemplates = mapping[reportGroupId][sheetId];
  const entry = Object.entries(reportTemplates).find(
    ([_, currentTransientId]) => currentTransientId === transientId,
  );

  return entry ? entry[0] : undefined;
}

/**
 * Gets the full mapping for a specific report group
 * @param storage The storage instance to use for operations
 * @param reportGroupId The ID of the report group
 * @returns The mapping for the report group
 */
export function getFullMapping(
  storage: TransientStorageBase<TransientIdMapping>,
  reportGroupId: string,
): TransientIdsBySheetId {
  const mapping = storage.getMapping();
  return mapping[reportGroupId] || {};
}

/**
 * Gets the full mapping for a specific report group
 * @param storage The storage instance to use for operations
 * @param reportGroupId The ID of the report group
 * @returns The mapping for the report group
 */
export function getFullOverrideMapping(
  storage: TransientStorageBase<TransientIdMapping>,
  reportGroupId: string,
): TransientOverrideBySheetId {
  const mapping = storage.getOverrideMapping();
  return mapping[reportGroupId] || {};
}

/**
 * Gets the transient ID mapping in a format suitable for GQL
 * @param storage The storage instance to use for operations
 * @param reportGroupId The ID of the report group
 * @returns The transient ID mapping in GQL format
 */
export function getGQLMapping(
  storage: TransientStorageBase<TransientIdMapping>,
  reportGroupId: string,
): TransientMappingGQL {
  const fullMapping = getFullMapping(storage, reportGroupId);
  const sheets: TransientMappingSheet[] = [];

  // Transform the mapping into the desired format
  Object.entries(fullMapping).forEach(([sheetId, reportTemplateMap]) => {
    const reportTemplates = Object.entries(reportTemplateMap).map(
      ([reportTemplateId, transientIds]) => ({
        id: reportTemplateId,
        transientIds,
      }),
    );

    if (reportTemplates.length > 0) {
      sheets.push({
        id: sheetId,
        reportTemplates,
      });
    }
  });

  return { sheets };
}

/**
 * Gets the total count of transient tables for a given reportGroupId and sheetId
 * @param storage The storage instance to use for operations
 * @param reportGroupId The ID of the report group
 * @param sheetId The ID of the sheet
 * @returns The total count of transient tables
 */
export function getTransientTableCount(
  storage: TransientStorageBase<TransientIdMapping>,
  reportGroupId: string,
  sheetId: string,
): number {
  const fullMapping = getFullMapping(storage, reportGroupId);

  if (!fullMapping[sheetId]) {
    return 0;
  }

  const reportTemplateMap = fullMapping[sheetId];
  let totalCount = 0;

  // Sum up all transient IDs across all report templates in the sheet
  Object.values(reportTemplateMap).forEach((transientIds) => {
    totalCount += transientIds.length;
  });

  return totalCount;
}

/**
 * Clears all transient IDs for the specified report group
 * @param storage The storage instance to use for operations
 * @param reportGroupId The ID of the report group
 */
export function clearAllTransientIds(
  storage: TransientStorageBase<TransientIdMapping>,
  reportGroupId: string,
): void {
  storage.clearReportGroup(reportGroupId);
}

/**
 * Clears all override transient IDs for the specified report group
 * @param storage The storage instance to use for operations
 * @param reportGroupId The ID of the report group
 */
export function clearAllOverrideTransientIds(
  storage: TransientStorageBase<TransientIdMapping>,
  reportGroupId: string,
): void {
  storage.clearOverrideReportGroup(reportGroupId);
}
